/**
 * What a `save_skill` offer would save: the drafted skill's title and
 * description, the built-in flow it adds to or the firm skill it changes,
 * its first lines (or the change, line by line), and why it was offered.
 *
 * `SkillOfferFollowThrough` opens the skill in Skills after an Edit in
 * Skills click, once the turn that saved it has run (before then the skill
 * is still a hidden proposal).
 */
import { useEffect, useRef, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { Tag } from "@/components/ui/tag";
import { automationsPath } from "@/features/automations/routes";
import { cn } from "@/lib/utils";

import type { SkillProposal } from "../offers/types";

type Props = { skill: SkillProposal };

export const SkillOfferPreview: FC<Props> = ({ skill }) => (
  <div
    className="mt-2 flex flex-col gap-1.5 rounded-md bg-fill-1 px-2.5 py-2"
    data-testid="skill-offer-preview"
    data-skill-mode={skill.mode}
  >
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <span className="min-w-0 truncate text-base font-medium text-fg">
        {skill.title}
      </span>
      {skill.revises ? (
        <Tag title={skill.revises.title}>Revises {skill.revises.title}</Tag>
      ) : skill.extends ? (
        <Tag title={skill.extends}>Adds to {skill.extends}</Tag>
      ) : (
        <Tag>New Automation</Tag>
      )}
    </div>
    {skill.description ? (
      <p className="text-sm text-fg-2">{skill.description}</p>
    ) : null}
    {skill.diff?.length ? (
      <ul
        className="flex flex-col font-mono text-sm"
        data-testid="skill-offer-diff"
      >
        {skill.diff.map((line, index) => (
          <li
            key={`${index}-${line.op}`}
            className={cn(
              "truncate rounded-xs px-1",
              line.op === "add"
                ? "bg-success-soft text-success"
                : "bg-danger-soft text-danger line-through"
            )}
            title={line.text}
          >
            {line.op === "add" ? "+ " : "− "}
            {line.text}
          </li>
        ))}
      </ul>
    ) : skill.changeNote ? (
      <p className="text-sm text-fg-3">{skill.changeNote}</p>
    ) : null}
    {skill.excerpt ? (
      <p
        className="line-clamp-6 text-sm whitespace-pre-wrap text-fg-3"
        data-testid="skill-offer-excerpt"
      >
        {skill.excerpt}
      </p>
    ) : null}
    {skill.reason ? (
      <p className="text-sm text-fg-3" data-testid="skill-offer-reason">
        {skill.reason}
      </p>
    ) : null}
  </div>
);

type FollowThroughProps = { skillId: string; busy: boolean };

export const SkillOfferFollowThrough: FC<FollowThroughProps> = ({
  skillId,
  busy
}) => {
  const navigate = useNavigate();
  const sawTurn = useRef(busy);
  const done = useRef(false);
  useEffect(() => {
    if (busy) {
      sawTurn.current = true;
      return;
    }
    if (!sawTurn.current || done.current) return;
    done.current = true;
    navigate(automationsPath({ skillId }));
  }, [busy, navigate, skillId]);
  return null;
};
